import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { AgentLinePlay } from '@/app/components/icons/AgentLinePlay';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { BaseVideoPlayer } from '@/app/components/shared/VideoPlayer/BaseVideoPlayer';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { type StoryboardItem, useFetch, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { VideoPlayerRef, ViewResourceItem } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { useCreateNewProject } from '../../../_hooks/use-project-store';
import type { AgentProjectShowCase } from '../../../_types';
import { ratioToWidthHeight } from '../../../_utils';
import { projectsUseShowCase } from '../../../apis';

export const LibraryProjectDialog = ({
  open,
  onClose,
  data,
}: {
  open: boolean;
  onClose: () => void;
  data: AgentProjectShowCase;
}) => {
  const t = useTranslations();
  const [isPaused, setIsPaused] = useState(false);
  const playerRef = useRef<VideoPlayerRef | null>(null);
  const { canCreateProject } = useCreateNewProject();
  const router = useRouter();
  const { openAgentViewResourceDialog } = useStore((state) => ({
    openAgentViewResourceDialog: state.openAgentViewResourceDialog,
  }));
  const isTeamSpace = useStore((state) => state.isTeamSpace);

  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { open: openAgentInviteDialog } = useGlobalModal('agentInviteDialog');

  const { project_name, description, input_video_urls, input_image_urls, input_video_cover_urls, scriptData, ratio } =
    data;
  const [wmWidth, wmHeight] = ratioToWidthHeight(ratio);

  const handleClickPlayButton = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    playerRef.current?.play();
    setIsPaused(false);
  };

  const { loading, run: createProjectByShowCase } = useFetch(
    (showCaseId: string) =>
      projectsUseShowCase({
        body: {
          show_case_id: showCaseId,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({ type: 'error', message: error.message });
          return;
        }
        if (data?.project_id) {
          onClose();
          router.push(`/project/${data?.project_id}`);
        }
      },
    },
  );

  const handleUseProject = async () => {
    const createProjectResult = await canCreateProject();
    if (createProjectResult === 'invited') {
      openAgentInviteDialog();
      reportEvent('agent_friends_invites_view', {
        refer_element: 'copy_usecase',
      });
      return false;
    }
    if (createProjectResult === 'upgrade') {
      if (isTeamSpace) {
        openConfirmDialog({
          title: t('popup.dialog.error.teamNeedUpgrade.title'),
          description: t('popup.dialog.error.teamNeedUpgrade.content'),
          primaryButtonText: t('popup.dialog.error.confirm'),
        });
      } else {
        openPlanDetailDialog({ scene: PlanDialogScene.AgentProject, fromPage: 'copy_usecase' });
      }
      reportEvent('vip_price_modal_exposure', {
        from_page: 'copy_usecase',
      });
      return false;
    }
    if (createProjectResult === 'purchase') {
      if (isTeamSpace) {
        openConfirmDialog({
          title: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.title'),
          description: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.content'),
          primaryButtonText: t('popup.dialog.error.confirm'),
        });
      } else {
        openPurchaseCreditsDialog({ fromAgentProject: true });
      }
      reportEvent('credit_modal_exposure', {
        from_page: 'copy_usecase',
      });
      return false;
    }
    if (data.id) {
      createProjectByShowCase(data.id);
    }
  };

  const handleClickInput = (id: string) => {
    const inputList: ViewResourceItem[] = [];
    if (input_video_urls?.length) {
      inputList.push(
        ...input_video_urls.map((url, index) => ({
          id: `video-${index}`,
          type: 'video' as const,
          url,
          poster: input_video_cover_urls?.[index] || '',
        })),
      );
    }
    if (input_image_urls?.length) {
      inputList.push(
        ...input_image_urls.map((url, index) => ({
          id: `image-${index}`,
          type: 'image' as const,
          url,
          poster: url,
        })),
      );
    }
    openAgentViewResourceDialog(inputList, id);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="flex h-full max-h-[700px] w-full max-w-[1110px] items-center justify-between gap-4 bg-[#17191B] p-0 md:p-0"
        closable={false}
      >
        <div className="relative h-full w-full flex-1 overflow-hidden p-4">
          <MediaWithWatermark width={wmWidth} height={wmHeight} showWatermark={SITE === 'china'}>
            <BaseVideoPlayer
              ref={playerRef}
              src={data?.video_url || ''}
              poster={data?.cover_url || ''}
              autoPlay={true}
              hasAudio={true}
              defaultVolume={0.5}
              className="size-full overflow-hidden rounded-16"
            />
            {isPaused && (
              <AgentLinePlay
                currentColor="#000"
                className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 z-10 size-12 cursor-pointer"
                onClick={(e: React.MouseEvent<SVGSVGElement>) => handleClickPlayButton(e)}
              />
            )}
          </MediaWithWatermark>
        </div>
        <div className="flex h-full w-[400px] flex-shrink-0 flex-col items-start justify-start gap-5 px-4 pt-3 pr-6 pb-6">
          <div className="flex w-full items-center justify-end">
            <BaseLineClose className="size-4 cursor-pointer text-system-white" onClick={onClose} />
          </div>
          <div className="py-4 font-semibold text-2xl text-system-white">{project_name}</div>

          <div className="scrollbar-hide flex flex-col gap-2 overflow-y-auto">
            <div className="flex flex-col gap-2">
              <div className="text-system-white36 text-xs">{t('agentProject.library.introduce')}</div>
              <div className="whitespace-pre-wrap text-sm text-system-white">{description}</div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-system-white36 text-xs">{t('agentProject.library.input')}</div>
              <div className="flex items-center gap-2">
                {input_video_cover_urls?.map((url, index) => (
                  <div
                    key={url}
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickInput(`video-${index}`);
                      setIsPaused(false);
                    }}
                  >
                    <img src={url} alt={`input-video-cover-${index}`} className="h-10 w-10" />
                  </div>
                ))}
                {input_image_urls?.map((url, index) => (
                  <div
                    key={url}
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickInput(`image-${index}`);
                      setIsPaused(false);
                    }}
                  >
                    <img src={url} alt={`input-image-${index}`} className="h-10 w-10" />
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-3 overflow-hidden text-sm">
                {/* 旁白部分 */}
                {scriptData?.full_narration && (
                  <div className="flex flex-col gap-1">
                    <div className="font-semibold text-system-white text-xs">{t('agentProject.scriptNode.aside')}</div>
                    {scriptData?.full_narration}
                  </div>
                )}

                {/* 分镜部分 */}
                {scriptData?.storyboard.map((item: StoryboardItem) => {
                  return (
                    <div key={item.part} className="flex flex-col gap-1">
                      <div className="font-semibold text-system-white text-xs">
                        {item.stage} : {item.duration}s
                      </div>
                      {item.description}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex w-full items-center justify-center">
            <Button
              variant="default"
              className="h-11 w-full rounded-12 bg-system-white px-8 font-semibold text-system-black"
              loading={loading}
              onClick={() => {
                handleUseProject();
                reportEvent('use_project_click', {
                  sample_project_id: data.id || '',
                });
              }}
            >
              {t('agentProject.library.use')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
